// @flow

// Libs
import React, { Component } from 'react'
import { Link } from 'react-router-dom'

// Utils
import url from 'hfty-utils/url'

// Components
import Button from '@react-mdc/button'

// Messages
import { FormattedMessage } from 'react-intl'
import messages from '../messages'

// Icons
import Map from 'fc-icons/src/Map'
import List from 'fc-icons/src/List'
import Comment from 'fc-icons/src/Comment'

// Constants
import {
  ATTR_SOIL_JOB_ID,
  ATTR_STATUS,
  ATTR_PAID,
  ATTR_MAP_VIEW,
  ATTR_LAB_RESULTS,
  ATTR_COMMENTS,
  ATTR_RECOMMENDATIONS,
  ATTR_REOPEN_JOB,
} from '../constants'
import {
  RESULTS_POSTED,
  SHIPPING,
  ORDER_TEST,
} from 'hfty-model-soil-jobs'
import { MSG_REOPEN_JOB_TABLE_ROW_BTN } from 'hfty-reopen-job-dom'
import { MAP_ROUTE_JOBS_TAB } from 'hfty-map-dom/src/constants'
import { COMMENTS_ROUTE } from 'hfty-comments-dom/src/constants'
import { RECOMMENDATIONS_ROUTE } from 'hfty-recommendations-dom'
import {
  HEFTY_SHIPPING_PDF_ROUTE,
  REPORTS_LAB_RESULTS_ROUTE,
} from 'hfty-soil-jobs-dom/src/constants'

// Columns config
import COLUMNS from 'hfty-datatable-soil-jobs/src/columnsConfig'

// Types
import type { SoilJobRecord } from '../../types'

// Styles
import { grey, white } from 'hfty-styles/src/colors'

const styles = {
  mapIcon: {
    padding: '2px 7px',
    cursor: 'pointer',
  },
  link: {
    textDecoration: 'none',
  },
  recordBtn: {
    display: 'block',
    padding: '3px 9px',
    marginBottom: 5,
    borderRadius: 3,
    backgroundColor: grey(0.8),
    color: white(),
  },
  reopenBtn: {
    minWidth: 'auto',
    height: 'auto',
    padding: '7px',
    fontSize: '1em',
    lineHeight: 'initial',
    color: white(),
    whiteSpace: 'nowrap',
  },
}

// Props type
type Props = {
  token: string,
  children?: any,
  actions: Object,
}

// Exports
export default function soilJobsTable (SoilJobDataTable: Function): Function {
  return class SoilJobsTable extends Component {
    props: Props
    renderer: { [string]: Function }

    constructor (props: Props) {
      super(props)

      this.renderer = {
        [ATTR_COMMENTS]: this.renderCommentIcon.bind(this),
        [ATTR_RECOMMENDATIONS]: this.renderRecommendationIcon.bind(this),
        [ATTR_MAP_VIEW]: this.renderMapIcon.bind(this),
        [ATTR_STATUS]: this.renderStatus.bind(this),
        [ATTR_LAB_RESULTS]: this.renderLabResults.bind(this),
        [ATTR_REOPEN_JOB]: this.renderReopenJob.bind(this),
      }
    }

    renderCommentIcon (record: SoilJobRecord): ?React.Element<any> {
      return record[ATTR_STATUS] === RESULTS_POSTED ? (
        <Link
          key={record[ATTR_SOIL_JOB_ID]}
          to={`${COMMENTS_ROUTE}/${record[ATTR_SOIL_JOB_ID]}`}
        >
          <Comment color={grey(0.8)} />
        </Link>
      ) : null
    }

    renderRecommendationIcon (record: SoilJobRecord): ?React.Element<any> {
      return record[ATTR_STATUS] === RESULTS_POSTED ? (
        <Link
          key={record[ATTR_SOIL_JOB_ID]}
          to={`${RECOMMENDATIONS_ROUTE}/${record[ATTR_SOIL_JOB_ID]}/`}
        >
          <List color={grey(0.8)} />
        </Link>
      ) : null
    }

    renderMapIcon (record: SoilJobRecord): ?React.Element<any> {
      return (
        <Link
          key={record[ATTR_SOIL_JOB_ID]}
          to={`${MAP_ROUTE_JOBS_TAB}/${record[ATTR_SOIL_JOB_ID]}`}
          onClick={() => {
            const jobId = record[ATTR_SOIL_JOB_ID]
            this.props.actions.preselectedJob(+jobId)
          }}
          style={styles.mapIcon}
        >
          <Map color={grey(0.8)} />
        </Link>
      )
    }

    renderStatus (record: SoilJobRecord): ?React.Element<any> {
      const isJobImported = record[ATTR_STATUS] === RESULTS_POSTED && !record[ATTR_PAID]

      return record[ATTR_STATUS] === SHIPPING || record[ATTR_STATUS] === RESULTS_POSTED ? (
        <div>
          {record[ATTR_STATUS]}
          <a
            href={url({
              path: `${HEFTY_SHIPPING_PDF_ROUTE}/${record[ATTR_SOIL_JOB_ID]}`,
              excludeTrailingSlash: true,
              token: this.props.token,
            })}
            download={'shipping.pdf'}
            target={'_blank'}
            style={styles.link}
          >{!isJobImported && '  >> Shipping PDF'}
          </a>
        </div>
      ) : record[ATTR_STATUS]
    }

    renderLabResults (record: SoilJobRecord): ?React.Element<any> {
      return record[ATTR_STATUS] === RESULTS_POSTED ? (
        <Link
          style={{...styles.link, ...styles.recordBtn}}
          to={`${REPORTS_LAB_RESULTS_ROUTE}/${record[ATTR_SOIL_JOB_ID]}`}
        >
          PDF
        </Link>
      ) : null
    }

    renderReopenJob (record: SoilJobRecord): ?React.Element<any> {
      return record[ATTR_STATUS] === ORDER_TEST ? (
        <Button
          raised
          primary
          style={styles.reopenBtn}
          onClick={(e: Event) => {
            e.stopPropagation()

            this.props.actions.reopenJob(record)
          }}
        >
          <FormattedMessage {...messages[MSG_REOPEN_JOB_TABLE_ROW_BTN]} />
        </Button>
      ) : null
    }

    render (): React.Element<any> {
      return (
        <SoilJobDataTable
          columns={COLUMNS}
          renderer={this.renderer}
          defaultColumn={ATTR_SOIL_JOB_ID}
          tools={this.props.children}
        />
      )
    }
  }
}
